
/* © 2017 - Copyright of Aetonix Systems Inc - All Rights Reserved. Patent pending.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Aetonix, January 01, 2017
 * For information or permission request, email info@aetonixsystems.com
 */

import React, {
	Component,
} from "react";
import {
	StyleSheet,
	View,
	Platform,
} from "react-native";
import {
	KeyboardAwareScrollView,
} from "react-native-keyboard-aware-scroll-view";

import ScrollIndicator from "ae-base/Base/ScrollIndicator";
import OfflineBanner from "../../Pages/Homepage/OfflineBanner";
import {
	KEYBOARD_EXTRA_SPACING_ANDROID,
	KEYBOARD_EXTRA_SPACING_IOS,
	KEYBOARD_EXTRA_HEIGHT_SPACING_ANDROID,
	KEYBOARD_EXTRA_HEIGHT_SPACING_IOS,
} from "../keyboardConstants";

var styles = StyleSheet.create({
	main: {
		flex: 1,
		flexShrink: 1,
	},
	scrollview: {
		flexGrow: 0,
		flexShrink: 1,
	},
	scrollviewFill: {
		flex: 1,
		flexGrow: 1,
	},
});

/**
 * MainScroll - A keyboard-aware scroll container component
 *
 * Wraps content in a KeyboardAwareScrollView that automatically adjusts when the keyboard appears,
 * ensuring text inputs remain visible and properly positioned. Handles platform-specific keyboard
 * behavior differences between iOS and Android.
 *
 * @component
 * @prop {boolean} [atouchaway] - If true and on Android, uses ScrollIndicator instead of keyboard-aware scrolling
 * @prop {object} [style] - Style object for the outer container
 * @prop {object} [scrollViewStyle] - Style object for the KeyboardAwareScrollView
 * @prop {object} [contentContainerStyle] - Style object for the scroll view's content container
 * @prop {boolean} [fillHeight] - If true, scroll view will flex to fill available height
 * @prop {boolean} [online] - Network connectivity status; when false, displays an offline banner
 * @prop {boolean} [showsVerticalScrollIndicator] - Whether to show the vertical scroll indicator
 * @prop {number} [extraScrollHeight] - Additional space (in pixels) to scroll when keyboard appears.
 *                                      Defaults to platform-specific values (140px Android, 20px iOS)
 * @prop {number} [extraHeight] - Additional height (in pixels) added to keyboard offset calculation.
 *                                Defaults to platform-specific values (140px Android, 0px iOS)
 * @prop {boolean} [enableOnAndroid] - Whether to enable keyboard awareness on Android.
 *                                     Defaults to true on Android, can be overridden to false
 * @prop {boolean} [enableResetScrollToCoords] - If false, prevents scroll view from resetting to top on input focus.
 *                                               Defaults to false on iOS, undefined on Android
 * @prop {ReactNode} children - Child components to render within the scroll view
 *
 * @example
 * <MainScroll
 *   online={props.online}
 *   extraScrollHeight={140}
 *   extraHeight={140}
 *   fillHeight
 * >
 *   {children}
 * </MainScroll>
 */
export default class MainScroll extends Component {
	constructor(props){
		super(props);
		this.scrollViewRef = React.createRef();
		this.scrollToTop = scrollToTop.bind(this);
	}

	render() {
		var props = this.props;
		var style = props.style;
		const reconnecting = (props.online !== undefined && props.online === false);
		const platformDefaultScrollSpacing = Platform.OS === "android" ? KEYBOARD_EXTRA_SPACING_ANDROID : KEYBOARD_EXTRA_SPACING_IOS;
		const platformDefaultHeightSpacing = Platform.OS === "android" ? KEYBOARD_EXTRA_HEIGHT_SPACING_ANDROID : KEYBOARD_EXTRA_HEIGHT_SPACING_IOS;
		const extraScrollHeight = props.extraScrollHeight ?? platformDefaultScrollSpacing;
		const extraHeight = props.extraHeight ?? platformDefaultHeightSpacing;

		return props.atouchaway && Platform.OS === "android" ? (
			<View style={{flex: 1}}>
				<ScrollIndicator style={style}>
					{reconnecting && <OfflineBanner />}
					{props.children}
				</ScrollIndicator>
			</View>
		) : (
			<View style={[styles.main, style]}>
				<KeyboardAwareScrollView
					keyboardDismissMode="none"
					automaticallyAdjustKeyboardInsets={false}
					extraScrollHeight={extraScrollHeight}
					extraHeight={extraHeight}
					keyboardOpeningTime={0}
					style={[
						styles.scrollview,
						props.fillHeight ? styles.scrollviewFill : null,
						props.scrollViewStyle,
					]}
					contentContainerStyle={props.contentContainerStyle}
					ref={this.scrollViewRef}
					showsVerticalScrollIndicator={props.showsVerticalScrollIndicator}
					keyboardShouldPersistTaps="handled"
					enableOnAndroid={props.enableOnAndroid ?? Platform.OS === "android"}
					{...(props.enableResetScrollToCoords !== undefined
						? { enableResetScrollToCoords: props.enableResetScrollToCoords }
						: Platform.OS === "ios"
							? { enableResetScrollToCoords: false }
							: {})}
				>
					<View onStartShouldSetResponder={() => true} >
						{reconnecting && <OfflineBanner />}
						{props.children}
					</View>
				</KeyboardAwareScrollView>
			</View>
		);
	}
}

function scrollToTop() {
	this.scrollViewRef?.current?.scrollToPosition(0, 0);
}
